/**
 * Mode-aware semantic accent colors for business components.
 *
 * Business surfaces are Tamagui/GB-based, so semantic accents (money-in
 * green, money-out red, warning amber, brand icons) must resolve from the
 * active Tamagui theme rather than the raw `palette` constants — the raw
 * values are the dark-mode picks (e.g. `palette.danger` #F0483E) and skip
 * the light theme's higher-contrast variants (#D62C23 negative, #E0A42E
 * warning, #059669 brand). The palette values remain only as fallbacks for
 * environments without a theme (defensive — should never happen in-app).
 */
import { useTheme } from 'tamagui';
import { palette } from '@greenbackapp/ui';

export interface SemanticColors {
  /** Money-in / success green (same value in both modes). */
  positive: string;
  /** Money-out / error red (darker in light mode for contrast). */
  negative: string;
  /** Caution amber (darker in light mode for contrast). */
  warning: string;
  /** Brand green for decorative icons (darker in light mode). */
  brand: string;
  /** De-emphasized icon color (tertiary text tone). */
  muted: string;
  /**
   * Recessive but readable (secondary text tone): for chart marks and labels
   * a reader still has to make out — a context series, a reference line, an
   * "everything else" slice. `muted` is too faint for those: the tertiary
   * tone is a disabled/hint color and fails contrast on a card in dark mode.
   */
  subdued: string;
}

export const useSemanticColors = (): SemanticColors => {
  const theme = useTheme();
  return {
    positive: String(theme.positive?.val ?? palette.success),
    negative: String(theme.negative?.val ?? palette.danger),
    warning: String(theme.warning?.val ?? palette.warning),
    brand: String(theme.brand?.val ?? palette.green),
    muted: String(theme.textTertiary?.val ?? palette.muted),
    subdued: String(theme.textSecondary?.val ?? palette.muted),
  };
};
